import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import './ProfilePage.css';

export default function ProfilePage() {
  const { user, signOut } = useAuth();
  const api = useApi();
  const [name, setName] = useState(user?.displayName || '');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [acceptToken, setAcceptToken] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [storageUsed, setStorageUsed] = useState(0);
  const [fileCount, setFileCount] = useState(0);
  const storageLimit = 1024 * 1024 * 1024; // 1GB

  useEffect(() => {
    api.get('/auth/me').then(r => {
      setStorageUsed(r.data.storage_used_bytes || 0);
      setFileCount(r.data.file_count || 0);
      if (r.data.display_name && !name) setName(r.data.display_name);
    }).catch(() => {});
  }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const saveProfile = async () => {
    setSaving(true);
    setError('');
    try {
      await api.patch('/auth/me', { name });
      setSuccess('Profile updated');
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const acceptInvite = async () => {
    if (!acceptToken.trim()) return;
    setAccepting(true);
    setError('');
    try {
      const res = await api.post('/orgs/invites/accept', { invite_token: acceptToken.trim() });
      setSuccess(`Joined organisation! Role: ${res.data.role}`);
      setAcceptToken('');
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Invalid or expired invite');
    } finally {
      setAccepting(false);
    }
  };

  const handleSignOut = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      await signOut();
    }
  };

  if (!user) return null;

  const usagePercent = Math.min((storageUsed / storageLimit) * 100, 100);

  return (
    <div className="profile-page">
      <h1 className="profile-title">Profile</h1>

      {error && <div className="profile-msg error">{error} <button onClick={() => setError('')}>✕</button></div>}
      {success && <div className="profile-msg success">{success} <button onClick={() => setSuccess('')}>✕</button></div>}

      <div className="card profile-card">
        <div className="profile-avatar-row">
          {user.photoURL
            ? <img src={user.photoURL} alt="avatar" className="profile-avatar" />
            : <div className="profile-avatar-placeholder">{(user.displayName || user.email || '?')[0].toUpperCase()}</div>
          }
          <div>
            <p className="profile-email">{user.email}</p>
            <p className="profile-uid">UID: {user.uid.slice(0, 16)}...</p>
          </div>
        </div>

        <div className="profile-field">
          <label>Display name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name..." />
        </div>

        <button className="btn btn-primary" onClick={saveProfile} disabled={saving}>
          {saving ? <><span className="spinner" /> Saving...</> : 'Save changes'}
        </button>
      </div>

      {/* Storage usage */}
      <div className="card profile-card">
        <h3 className="profile-section-title">Storage</h3>
        <div className="storage-meter">
          <div className="storage-bar">
            <div className="storage-fill" style={{ width: `${usagePercent}%` }} />
          </div>
          <p className="storage-text">{formatSize(storageUsed)} of {formatSize(storageLimit)} used · {fileCount} file{fileCount !== 1 ? 's' : ''}</p>
        </div>
      </div>

      <div className="card profile-card">
        <h3 className="profile-section-title">Accept Organisation Invite</h3>
        <p className="profile-section-desc">Paste an invite token you received to join an organisation.</p>
        <div className="profile-invite-row">
          <input value={acceptToken} onChange={e => setAcceptToken(e.target.value)} placeholder="Paste invite token..." style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={acceptInvite} disabled={accepting || !acceptToken.trim()}>
            {accepting ? <span className="spinner" /> : 'Join'}
          </button>
        </div>
      </div>

      <div className="card profile-card">
        <h3 className="profile-section-title">Account</h3>
        <button className="btn btn-danger" onClick={handleSignOut}>Sign out</button>
      </div>
    </div>
  );
}
